import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Space, Input, Select, message, Tooltip, Popconfirm, Modal } from 'antd';
import { QuestionCircleOutlined, ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined, TagsOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { faqsApi, faqCategoriesApi, show422Error, normalizeFaqApiBody } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { useListReturnState } from '../../hooks/useListReturnState';

const { Option } = Select;

const SuperadminFaqsSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [allFaqs, setAllFaqs] = useState([]);
  const [categories, setCategories] = useState([]);

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('all');

  const captureCurrentListState = useCallback(
    () => ({
      search,
      categoryId,
    }),
    [search, categoryId],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.search != null) setSearch(restored.search);
    if (restored.categoryId) setCategoryId(restored.categoryId);
  });

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [catName, setCatName] = useState('');
  const [catEditingId, setCatEditingId] = useState(null);

  const loadIndex = useCallback(async () => {
    setLoading(true);
    try {
      const res = await faqsApi.index();
      const root = normalizeFaqApiBody(res);
      setAllFaqs(root.faqs || []);
      setCategories(root.categories || []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت سوالات متداول');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSearch = useCallback(async (term) => {
    setLoading(true);
    try {
      const res = await faqsApi.search(term);
      const root = normalizeFaqApiBody(res);
      setAllFaqs(root.faqs || []);
    } catch (error) {
      message.error(error?.message || 'خطا در جستجو');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIndex();
  }, [loadIndex]);

  useEffect(() => {
    const q = search.trim();
    if (!q) {
      loadIndex();
      return;
    }
    const t = setTimeout(() => {
      loadSearch(q);
    }, 350);
    return () => clearTimeout(t);
  }, [search, loadIndex, loadSearch]);

  const filteredFaqs = useMemo(() => {
    const cid = String(categoryId);
    if (cid === 'all') return allFaqs;
    return (allFaqs || []).filter((f) => String(f?.faq_category_id) === cid || String(f?.category?.id) === cid);
  }, [allFaqs, categoryId]);

  const openCategories = async () => {
    setCatModalOpen(true);
    setCatEditingId(null);
    setCatName('');
    setCatLoading(true);
    try {
      const res = await faqCategoriesApi.getCreateMeta();
      const root = normalizeFaqApiBody(res);
      setCategories(root.categories || []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت دسته‌بندی‌ها');
    } finally {
      setCatLoading(false);
    }
  };

  const onDeleteFaq = async (id) => {
    try {
      await faqsApi.delete(id);
      message.success('سوال حذف شد');
      loadIndex();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    }
  };

  const onCreateCategory = async () => {
    const name = catName.trim();
    if (!name) return;
    setCatLoading(true);
    try {
      await faqCategoriesApi.create({ name });
      message.success('دسته‌بندی ایجاد شد');
      setCatName('');
      const res = await faqCategoriesApi.getCreateMeta();
      const root = normalizeFaqApiBody(res);
      setCategories(root.categories || []);
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error);
      else message.error(error?.message || 'خطا در ایجاد دسته‌بندی');
    } finally {
      setCatLoading(false);
    }
  };

  const onSaveCategory = async () => {
    const name = catName.trim();
    if (!catEditingId || !name) return;
    setCatLoading(true);
    try {
      await faqCategoriesApi.update(catEditingId, { name });
      message.success('دسته‌بندی بروزرسانی شد');
      setCatEditingId(null);
      setCatName('');
      const res = await faqCategoriesApi.getCreateMeta();
      const root = normalizeFaqApiBody(res);
      setCategories(root.categories || []);
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error);
      else message.error(error?.message || 'خطا در بروزرسانی دسته‌بندی');
    } finally {
      setCatLoading(false);
    }
  };

  const onDeleteCategory = async (id) => {
    setCatLoading(true);
    try {
      await faqCategoriesApi.delete(id);
      message.success('دسته‌بندی حذف شد');
      const res = await faqCategoriesApi.getCreateMeta();
      const root = normalizeFaqApiBody(res);
      setCategories(root.categories || []);
    } catch (error) {
      message.error(error?.message || 'خطا در حذف دسته‌بندی');
    } finally {
      setCatLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'عنوان',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: (t, r) => (
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-800 dark:text-gray-100" style={{ fontFamily: 'CustomFont' }}>
              {t || '—'}
            </div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
              {r?.category?.name || '—'}
            </div>
          </div>
        ),
      },
      {
        title: 'تصویر',
        key: 'image',
        width: 80,
        responsive: ['md'],
        render: (_, r) =>
          r?.image_url ? (
            <span className="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
              <img src={r.image_url} alt="" className="block h-10 w-10 object-cover" />
            </span>
          ) : (
            '—'
          ),
      },
      {
        title: 'عملیات',
        key: 'actions',
        width: 180,
        align: 'center',
        render: (_, r) => (
          <div className="flex w-full justify-center">
            <Space size="small">
              <Tooltip title="مشاهده">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => navigateToDetail(`/settings/superadmin-faqs/${r.id}`)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                />
              </Tooltip>
              <Tooltip title="ویرایش">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigateToDetail(`/settings/superadmin-faqs/${r.id}/edit`)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                />
              </Tooltip>
              <Popconfirm title="حذف این سوال؟" okText="حذف" cancelText="لغو" onConfirm={() => onDeleteFaq(r.id)}>
                <Tooltip title="حذف">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          </div>
        ),
      },
    ],
    [navigateToDetail, onDeleteFaq],
  );

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/25 flex-shrink-0">
                <QuestionCircleOutlined className="text-lg sm:text-xl text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">سوالات متداول مدیر</h1>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm m-0 truncate">مدیریت FAQ و دسته‌بندی‌ها</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<TagsOutlined />} onClick={openCategories} loading={catLoading}>
                دسته‌بندی‌ها
              </Button>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={loadIndex}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={() => navigate('/settings/superadmin-faqs/create')}>
                سوال جدید
              </Button>
            </Space>
          </div>

          <div className="bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50 rounded-b-xl">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجو..."
                prefix={<SearchOutlined className="text-gray-400" />}
                className="rounded-lg"
                style={{ fontFamily: 'CustomFont', maxWidth: 360, width: '100%', minWidth: 220 }}
                size="middle"
              />
              <Select
                value={categoryId}
                onChange={setCategoryId}
                popupClassName="custom-font"
                showSearch
                optionFilterProp="children"
                style={{ minWidth: 220, width: '100%', maxWidth: 320, fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه دسته‌بندی‌ها</Option>
                {categories.map((c) => (
                  <Option key={c.id} value={String(c.id)}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </div>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              {loading ? (
                <div className="p-4 sm:p-5">
                  <TableSkeleton rows={8} columns={3} />
                </div>
              ) : (
                <Table
                  rowKey="id"
                  loading={false}
                  columns={columns}
                  dataSource={filteredFaqs}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    responsive: true,
                  }}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </Card>
          </div>
        </Card>
      </div>

      <Modal
        wrapClassName="custom-font"
        classNames={{ body: 'custom-font' }}
        title={
          <div className="flex items-center gap-3 py-1">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-900/25">
              <TagsOutlined className="text-lg text-amber-600 dark:text-amber-400" />
            </span>
            <div className="min-w-0 text-right">
              <div className="text-base font-bold text-gray-800 dark:text-gray-100">دسته‌بندی‌های سوالات متداول</div>
              <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">افزودن، ویرایش و حذف دسته‌ها</div>
            </div>
          </div>
        }
        open={catModalOpen}
        onCancel={() => {
          setCatModalOpen(false);
          setCatEditingId(null);
          setCatName('');
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div className="flex flex-col gap-3" style={{ fontFamily: 'CustomFont' }}>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">نام دسته‌بندی</div>
              <Input
                value={catName}
                onChange={(e) => setCatName(e.target.value)}
                placeholder="مثلاً عمومی"
                style={{ fontFamily: 'CustomFont' }}
              />
            </div>
            {catEditingId ? (
              <Space>
                <Button type="primary" loading={catLoading} onClick={onSaveCategory} className={PANEL_PRIMARY_BTN_CLASS}>
                  ذخیره
                </Button>
                <Button
                  onClick={() => {
                    setCatEditingId(null);
                    setCatName('');
                  }}
                >
                  انصراف
                </Button>
              </Space>
            ) : (
              <Button type="primary" loading={catLoading} onClick={onCreateCategory} className={PANEL_PRIMARY_BTN_CLASS}>
                افزودن
              </Button>
            )}
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
            <Table
              rowKey="id"
              size="small"
              loading={catLoading}
              dataSource={categories}
              pagination={false}
              className="custom-font"
              columns={[
                {
                  title: 'دسته‌بندی',
                  dataIndex: 'name',
                  key: 'name',
                  render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>,
                },
                {
                  title: 'عملیات',
                  key: 'actions',
                  width: 120,
                  align: 'center',
                  render: (_, r) => (
                    <Space size="small">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setCatEditingId(r.id);
                          setCatName(r.name || '');
                        }}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                      />
                      <Popconfirm
                        title="حذف دسته‌بندی؟"
                        okText="حذف"
                        cancelText="لغو"
                        okButtonProps={{ danger: true, style: { fontFamily: 'CustomFont' } }}
                        cancelButtonProps={{ style: { fontFamily: 'CustomFont' } }}
                        onConfirm={() => onDeleteCategory(r.id)}
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
};

export default SuperadminFaqsSettings;
